import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import AsyncStorage from '@react-native-async-storage/async-storage';

type AccessibilitySettings = {
  colorBlindMode: boolean;
  colorBlindType:
    | 'protanopia'
    | 'deuteranopia'
    | 'tritanopia'
    | 'achromatopsia'
    | 'none';
  highContrast: boolean;
  largeText: boolean;
  voiceGuidance: boolean;
  showColorNames: boolean;
};

// Query keys
export const accessibilityKeys = {
  all: ['accessibility'] as const,
  settings: () => [...accessibilityKeys.all, 'settings'] as const,
};

const defaultSettings: AccessibilitySettings = {
  colorBlindMode: false,
  colorBlindType: 'none',
  highContrast: false,
  largeText: false,
  voiceGuidance: false,
  showColorNames: false,
};

// Hook to get accessibility settings
const useAccessibilitySettings = () => {
  return useQuery({
    queryKey: accessibilityKeys.settings(),
    queryFn: async () => {
      try {
        const storedSettings = await AsyncStorage.getItem(
          'accessibilitySettings'
        );
        if (storedSettings) {
          return JSON.parse(storedSettings) as AccessibilitySettings;
        }
        return defaultSettings;
      } catch (error) {
        console.error('Failed to load accessibility settings:', error);
        return defaultSettings;
      }
    },
    staleTime: Infinity,
    gcTime: Infinity,
  });
};

// Main accessibility hook
export const useAccessibility = () => {
  const queryClient = useQueryClient();
  const { data: settings = defaultSettings } = useAccessibilitySettings();

  // Update settings mutation
  const updateSettingsMutation = useMutation({
    mutationFn: async (newSettings: Partial<AccessibilitySettings>) => {
      const updatedSettings = { ...settings, ...newSettings };
      try {
        await AsyncStorage.setItem(
          'accessibilitySettings',
          JSON.stringify(updatedSettings)
        );
        return updatedSettings;
      } catch (error) {
        console.error('Failed to save accessibility settings:', error);
        throw error;
      }
    },
    onSuccess: (data) => {
      queryClient.setQueryData(accessibilityKeys.settings(), data);
    },
  });

  // Reset settings mutation
  const resetSettingsMutation = useMutation({
    mutationFn: async () => {
      try {
        await AsyncStorage.setItem(
          'accessibilitySettings',
          JSON.stringify(defaultSettings)
        );
        return defaultSettings;
      } catch (error) {
        console.error('Failed to reset accessibility settings:', error);
        throw error;
      }
    },
    onSuccess: (data) => {
      queryClient.setQueryData(accessibilityKeys.settings(), data);
    },
  });

  // Helper function to adjust colors for colorblind users
  const adjustColorForAccessibility = (hexColor: string): string => {
    if (!settings.colorBlindMode || settings.colorBlindType === 'none') {
      return hexColor;
    }

    // Convert hex to RGB
    const r = parseInt(hexColor.slice(1, 3), 16);
    const g = parseInt(hexColor.slice(3, 5), 16);
    const b = parseInt(hexColor.slice(5, 7), 16);

    let adjustedR = r;
    let adjustedG = g;
    let adjustedB = b;

    // Apply color transformations based on colorblind type
    switch (settings.colorBlindType) {
      case 'protanopia': // Red-blind
        adjustedR = Math.floor(0.56667 * r + 0.43333 * g);
        adjustedG = Math.floor(0.55833 * r + 0.44167 * g);
        adjustedB = Math.floor(0.24167 * r + 0.75833 * b);
        break;
      case 'deuteranopia': // Green-blind
        adjustedR = Math.floor(0.625 * r + 0.375 * g);
        adjustedG = Math.floor(0.70833 * r + 0.29167 * g);
        adjustedB = Math.floor(0.24167 * g + 0.75833 * b);
        break;
      case 'tritanopia': // Blue-blind
        adjustedR = Math.floor(0.95 * r + 0.05 * g);
        adjustedG = Math.floor(0.16667 * r + 0.83333 * g);
        adjustedB = Math.floor(0.5 * g + 0.5 * b);
        break;
      case 'achromatopsia': {
        // Complete color blindness (grayscale)
        const gray = Math.floor(0.299 * r + 0.587 * g + 0.114 * b);
        adjustedR = gray;
        adjustedG = gray;
        adjustedB = gray;
        break;
      }
    }

    // Convert back to hex
    const toHex = (c: number) => {
      const hex = Math.min(255, Math.max(0, Math.round(c))).toString(16);
      return hex.length === 1 ? '0' + hex : hex;
    };

    return `#${toHex(adjustedR)}${toHex(adjustedG)}${toHex(adjustedB)}`;
  };

  return {
    settings,
    updateSettings: updateSettingsMutation.mutateAsync,
    resetSettings: resetSettingsMutation.mutateAsync,
    adjustColorForAccessibility,
  };
};
